/**
 * LiveChat集成相关的TypeScript类型定义
 */

// LiveChat配置接口
export interface LiveChatConfig {
  /** LiveChat License ID */
  licenseId: string
  /** LiveChat Group ID (可选) */
  groupId?: string
  /** 是否启用LiveChat功能 */
  enabled: boolean
}

// LiveChat用户信息接口
export interface LiveChatUser {
  /** 用户ID */
  id: string
  /** 用户名 */
  name: string
  /** 用户邮箱 */
  email: string
  /** 用户注册时间 */
  registeredAt: string
}

// LiveChat API响应接口
export interface LiveChatConfigResponse {
  licenseId: string
  groupId: string
  enabled: boolean
}

// LiveChat配置更新请求接口
export interface LiveChatConfigUpdateRequest {
  license_id: string
  group_id?: string
  enabled: boolean
}

// LiveChat初始化选项
export interface LiveChatInitOptions {
  /** 用户信息 */
  user?: LiveChatUser | null
  /** 是否自动打开聊天窗口 */
  autoOpen?: boolean
}

// LiveChat自定义变量
export interface LiveChatCustomVariable {
  name: string
  value: string
}

// LiveChat状态枚举
export enum LiveChatStatus {
  /** 未初始化 */
  NOT_INITIALIZED = 'not_initialized',
  /** 初始化中 */
  INITIALIZING = 'initializing',
  /** 已初始化 */
  INITIALIZED = 'initialized',
  /** 初始化失败 */
  FAILED = 'failed'
}

// LiveChat错误类型
export interface LiveChatError {
  code: string
  message: string
  details?: any
}

// 聊天消息类型
export interface ChatMessage {
  /** 消息ID */
  id: string
  /** 消息内容 */
  content: string
  /** 消息类型 */
  type: 'text' | 'image' | 'system'
  /** 是否为用户发送的消息 */
  isUser: boolean
  /** 消息时间戳 */
  timestamp: number
  /** 消息状态 */
  status?: 'sending' | 'sent' | 'failed'
  /** 图片URL（仅图片消息） */
  imageUrl?: string
  /** 是否已读 */
  read?: boolean
  /** 发送者信息 */
  sender?: {
    id: string
    name: string
    avatar?: string
  }
}

// 聊天会话类型
export interface ChatSession {
  /** 会话ID */
  id: string
  /** 用户ID（可选，访客模式下为空） */
  userId?: string
  /** 创建时间 */
  createdAt: string
  /** 会话状态 */
  status: 'active' | 'closed'
  /** 最后活动时间 */
  lastActivity?: string
}

// 连接状态类型
export type ConnectionStatus = 'connected' | 'connecting' | 'disconnected' | 'error'

// 聊天配置类型
export interface ChatConfig {
  /** 是否启用自定义聊天界面 */
  enabled: boolean
  /** 最大文件大小（字节） */
  maxFileSize: number
  /** 允许的文件类型 */
  allowedFileTypes: string[]
  /** 最大消息长度 */
  maxMessageLength: number
  /** 消息历史保留天数 */
  historyRetentionDays: number
}

// 发送消息请求类型
export interface SendMessageRequest {
  /** 会话ID */
  sessionId: string
  /** 消息内容 */
  content: string
  /** 消息类型 */
  type: 'text' | 'image'
  /** 图片URL（仅图片消息） */
  imageUrl?: string
  /** 用户信息 */
  user: LiveChatUser | null
}

// 文件上传响应类型
export interface UploadFileResponse {
  /** 文件URL */
  url: string
  /** 文件ID */
  fileId: string
  /** 文件名 */
  fileName: string
  /** 文件大小 */
  fileSize: number
}

// 聊天历史存储类型
export interface ChatHistoryStorage {
  /** 会话ID */
  sessionId: string
  /** 消息列表 */
  messages: ChatMessage[]
  /** 存储时间戳 */
  timestamp: number
  /** 用户ID */
  userId?: string
}

// 聊天界面配置类型
export interface ChatInterfaceConfig {
  /** 是否启用自定义界面 */
  useCustomInterface: boolean
  /** 界面主题 */
  theme?: 'light' | 'dark'
  /** 是否显示头像 */
  showAvatar?: boolean
  /** 是否显示时间戳 */
  showTimestamp?: boolean
  /** 消息气泡样式 */
  bubbleStyle?: 'rounded' | 'square'
}

// 消息发送选项
export interface MessageSendOptions {
  /** 是否立即发送 */
  immediate?: boolean
  /** 重试次数 */
  retryCount?: number
  /** 超时时间（毫秒） */
  timeout?: number
}

// 文件验证结果
export interface FileValidationResult {
  /** 是否有效 */
  isValid: boolean
  /** 错误信息 */
  error?: string
  /** 文件信息 */
  fileInfo?: {
    name: string
    size: number
    type: string
  }
}

// WebSocket消息类型
export interface WebSocketMessage {
  /** 消息类型 */
  type: 'message' | 'typing' | 'status' | 'error'
  /** 消息数据 */
  data: any
  /** 时间戳 */
  timestamp: number
}

// 聊天统计信息
export interface ChatStats {
  /** 总消息数 */
  totalMessages: number
  /** 用户消息数 */
  userMessages: number
  /** 客服消息数 */
  agentMessages: number
  /** 未读消息数 */
  unreadMessages: number
  /** 会话开始时间 */
  sessionStartTime?: number
  /** 最后活动时间 */
  lastActivityTime?: number
}